// frontend/src/lib/partner-api.ts
/**
 * API client functions for the Partner Portal.
 * Uses the existing apiClient from api-client.ts for authenticated requests.
 */

import { apiClient } from './api-client'
import apiClientFetch from './api-client'
import type {
  Agent,
  AgentCategory,
  AgentCategoryCreate,
  AgentCategoryUpdate,
  AgentCreate,
  AgentUpdate,
  ContentResponse,
  DashboardStats,
  Deal,
  DealCreate,
  DealUpdate,
  Notification,
  Partner,
  PartnerUpdate,
} from '@/types/partner-types'

// =============================================================================
// DEALS API
// =============================================================================

export const dealsApi = {
  /** Get dashboard stats (scoped by role) */
  getStats: () => apiClient.get<DashboardStats>('/api/deals/stats'),

  /** List deals with optional filters */
  list: (params?: { status?: string; search?: string; skip?: number; limit?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.skip != null) searchParams.set('skip', String(params.skip))
    if (params?.limit != null) searchParams.set('limit', String(params.limit))
    const qs = searchParams.toString()
    return apiClient.get<Deal[]>(`/api/deals${qs ? `?${qs}` : ''}`)
  },

  /** Register a new deal */
  create: (data: DealCreate) => apiClient.post<Deal>('/api/deals', data),

  /** Get deal by ID */
  getById: (id: number) => apiClient.get<Deal>(`/api/deals/${id}`),

  /** Update a deal */
  update: (id: number, data: DealUpdate) =>
    apiClient.patch<Deal>(`/api/deals/${id}`, data),

  /** Delete a deal (Admin only) */
  delete: (id: number) => apiClient.delete(`/api/deals/${id}`),
}

// =============================================================================
// PARTNERS API
// =============================================================================

export const partnersApi = {
  /** Get current user's partner profile */
  getMyProfile: () => apiClient.get<Partner>('/api/partners/me'),

  /** Update current user's partner profile */
  updateMyProfile: (data: PartnerUpdate) =>
    apiClient.put<Partner>('/api/partners/me', data),

  /** List all partners (admin only) */
  list: (params?: { search?: string; skip?: number; limit?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.set('search', params.search)
    if (params?.skip != null) searchParams.set('skip', String(params.skip))
    if (params?.limit != null) searchParams.set('limit', String(params.limit))
    const qs = searchParams.toString()
    return apiClient.get<Partner[]>(`/api/partners${qs ? `?${qs}` : ''}`)
  },

  /** Get partner by ID (admin only) */
  getById: (id: number) => apiClient.get<Partner>(`/api/partners/${id}`),

  /** Update partner (admin only) */
  update: (id: number, data: PartnerUpdate) =>
    apiClient.patch<Partner>(`/api/partners/${id}`, data),
}

// =============================================================================
// AGENTS API
// =============================================================================

export const agentsApi = {
  // --- Categories ---

  /** List all categories with agent counts */
  listCategories: () =>
    apiClient.get<AgentCategory[]>('/api/agents/categories'),

  /** Create a new category (admin only) */
  createCategory: (data: AgentCategoryCreate) =>
    apiClient.post<AgentCategory>('/api/agents/categories', data),

  /** Update a category (admin only) */
  updateCategory: (id: number, data: AgentCategoryUpdate) =>
    apiClient.put<AgentCategory>(`/api/agents/categories/${id}`, data),

  /** Delete a category (admin only) */
  deleteCategory: (id: number) =>
    apiClient.delete(`/api/agents/categories/${id}`),

  /** Upload category image (admin only) */
  uploadCategoryImage: async (categoryId: number, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    // Use raw fetch to avoid JSON.stringify on FormData
    return apiClientFetch<{ image_url: string }>(
      `/api/agents/categories/${categoryId}/image`,
      { method: 'POST', body: formData }
    )
  },

  // --- Agents ---

  /** List agents with optional filters */
  list: (params?: { category_id?: number; search?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.category_id != null)
      searchParams.set('category_id', String(params.category_id))
    if (params?.search) searchParams.set('search', params.search)
    const qs = searchParams.toString()
    return apiClient.get<Agent[]>(`/api/agents${qs ? `?${qs}` : ''}`)
  },

  /** Get agent by ID */
  getById: (id: number) => apiClient.get<Agent>(`/api/agents/${id}`),

  /** Create a new agent (admin only) */
  create: (data: AgentCreate) => apiClient.post<Agent>('/api/agents', data),

  /** Update an agent (admin only) */
  update: (id: number, data: AgentUpdate) =>
    apiClient.put<Agent>(`/api/agents/${id}`, data),

  /** Delete an agent (admin only) */
  delete: (id: number) => apiClient.delete(`/api/agents/${id}`),

  /** Upload agent resource file (admin only) */
  uploadResource: async (agentId: number, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    // Use raw fetch to avoid JSON.stringify on FormData
    return apiClientFetch<{ url: string; original_name: string; size: number }>(
      `/api/agents/${agentId}/resources`,
      { method: 'POST', body: formData }
    )
  },
}

// =============================================================================
// CONTENT API
// =============================================================================

export const contentApi = {
  /** Get content by key */
  get: (key: string) => apiClient.get<ContentResponse>(`/api/content/${key}`),

  /** Update content (admin only) */
  update: (key: string, value: Record<string, unknown>) =>
    apiClient.put<ContentResponse>(`/api/content/${key}`, { value }),
}

// =============================================================================
// NOTIFICATIONS API
// =============================================================================

export const notificationsApi = {
  /** Get notifications (unread first) */
  list: (params?: { skip?: number; limit?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.skip != null) searchParams.set('skip', String(params.skip))
    if (params?.limit != null) searchParams.set('limit', String(params.limit))
    const qs = searchParams.toString()
    return apiClient.get<Notification[]>(
      `/api/notifications${qs ? `?${qs}` : ''}`
    )
  },

  /** Get unread count */
  getUnreadCount: () =>
    apiClient.get<{ count: number }>('/api/notifications/unread-count'),

  /** Mark a notification as read */
  markAsRead: (id: number) =>
    apiClient.patch<Notification>(`/api/notifications/${id}/read`, {}),

  /** Mark all notifications as read */
  markAllAsRead: () =>
    apiClient.patch<{ message: string }>('/api/notifications/read-all', {}),
}
